/**
 * TrustedProvidersCity.jsx
 *
 * SEO landing page for trusted-independent providers in a specific city.
 * URL: /trusted-providers/:citySlug
 *
 * Why: city-segmented pages let us rank for "independent vet [city]"
 * long-tail queries — exactly the kind of search a service-oriented dog
 * owner makes. Each page becomes a sitemap entry; pages without any
 * verified providers in that city render a friendly "nothing yet — nominate
 * one" CTA (with noindex to avoid thin-content SEO penalty).
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, MapPin, Plus, ExternalLink, Star, BadgeCheck } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import SEO from '../components/SEO';
import { useAuth } from '../context/AuthContext';
import NominateProviderDialog from '../components/independents/NominateProviderDialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Reverse-lookup the city slug back to its display city + state. We hit
// /api/independents/cities once and find the matching slug.
function useCityFromSlug(citySlug) {
  const [city, setCity] = useState(null);
  useEffect(() => {
    if (!citySlug) return;
    axios.get(`${API}/independents/cities?limit=500`)
      .then((r) => {
        const match = (r.data || []).find((c) => c.slug === citySlug);
        setCity(match || { city: null, state: null });
      })
      .catch(() => setCity({ city: null, state: null }));
  }, [citySlug]);
  return city;
}

export default function TrustedProvidersCity() {
  const { citySlug } = useParams();
  const { user } = useAuth();
  const cityMeta = useCityFromSlug(citySlug);
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [nominateOpen, setNominateOpen] = useState(false);

  useEffect(() => {
    if (!cityMeta) return;
    if (!cityMeta.city) { setLoading(false); return; }
    setLoading(true);
    axios.get(`${API}/independents`, { params: { search: cityMeta.city } })
      .then((r) => setProviders((r.data || []).filter((p) => p.city === cityMeta.city)))
      .catch(() => toast.error('Could not load providers'))
      .finally(() => setLoading(false));
  }, [cityMeta]);

  const displayCity = cityMeta?.city;
  const displayState = cityMeta?.state;
  const headline = useMemo(() => {
    if (!displayCity) return 'City not found';
    return `Trusted Independent Pet Providers in ${displayCity}${displayState ? `, ${displayState}` : ''}`;
  }, [displayCity, displayState]);

  // Empty city → noindex (thin content), still helpful to the user
  if (!loading && cityMeta && !cityMeta.city) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-16" data-testid="city-not-found">
        <SEO title="City Not Found" noindex url={`/trusted-providers/${citySlug}`} />
        <Card>
          <CardContent className="p-8 text-center space-y-4">
            <h1 className="text-2xl font-bold" data-testid="city-not-found-title">We don't have providers there yet</h1>
            <p className="text-stone-600">Be the first to nominate a trusted independent provider in your area.</p>
            <div className="flex justify-center gap-2 pt-2">
              <Link to="/trusted-providers"><Button variant="outline" data-testid="back-to-directory"><ArrowLeft className="w-4 h-4 mr-2" /> Back to directory</Button></Link>
              <Button onClick={() => user ? setNominateOpen(true) : toast.error('Please sign in')} data-testid="city-nominate-btn"><Plus className="w-4 h-4 mr-2" /> Nominate</Button>
            </div>
          </CardContent>
        </Card>
        <NominateProviderDialog
          open={nominateOpen}
          onClose={() => setNominateOpen(false)}
          onSubmitted={() => { setNominateOpen(false); toast.success('Thanks — admins will review shortly.'); }}
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-stone-50 to-white" data-testid="city-page">
      {displayCity && (
        <SEO
          title={`Trusted Independent Pet Providers in ${displayCity}${displayState ? `, ${displayState}` : ''}`}
          description={`Member-vouched independent and family-owned vets, stores, and trainers in ${displayCity}. Not owned by private equity. ${providers.length} providers listed.`}
          url={`/trusted-providers/${citySlug}`}
          keywords={[`independent vet ${displayCity}`, `family-owned pet store ${displayCity}`, `non-corporate vet ${displayCity}`, `${displayCity} dog trainer`, 'AADC trusted providers']}
        />
      )}

      <section className="max-w-4xl mx-auto px-4 sm:px-6 pt-10 pb-6">
        <Link to="/trusted-providers" className="inline-flex items-center gap-1 text-sm text-stone-500 hover:text-stone-800" data-testid="city-back-link">
          <ArrowLeft className="w-4 h-4" /> All cities
        </Link>
        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight mt-3" data-testid="city-headline">{headline}</h1>
        <p className="mt-3 text-base text-stone-600">
          Vets, stores, and trainers our members vouch for in {displayCity || 'this area'} — independently or
          family-owned, not rolled up by private equity.
        </p>
      </section>

      <section className="max-w-4xl mx-auto px-4 sm:px-6 pb-16 space-y-4">
        {loading && <div className="text-stone-500" data-testid="city-loading">Loading…</div>}
        {!loading && providers.length === 0 && (
          <Card>
            <CardContent className="p-8 text-center space-y-3" data-testid="city-empty">
              <p className="text-stone-600">No providers in {displayCity} yet. Be the first to nominate.</p>
              <Button onClick={() => user ? setNominateOpen(true) : toast.error('Please sign in')}><Plus className="w-4 h-4 mr-2" /> Nominate one</Button>
            </CardContent>
          </Card>
        )}
        {!loading && providers.map((p) => (
          <Card key={p.id} className="border-stone-200" data-testid={`city-provider-${p.id}`}>
            <CardContent className="p-5">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    {p.status === 'featured' && <BadgeCheck className="w-4 h-4 text-amber-600" />}
                    <h2 className="font-semibold text-lg">{p.name}</h2>
                  </div>
                  <div className="text-sm text-stone-500 mt-1 flex flex-wrap items-center gap-x-3">
                    <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" /> {p.city}{p.state ? `, ${p.state}` : ''}</span>
                    {p.founded_year && <span>est. {p.founded_year}</span>}
                  </div>
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    <Badge className="bg-amber-100 text-amber-900 border-amber-200 border text-xs">{p.ownership_type.replace('_', '-')}</Badge>
                    {p.quality_certifications?.slice(0, 3).map((c) => <Badge key={c} variant="outline" className="text-xs">{c}</Badge>)}
                  </div>
                  {p.service_notes && <p className="text-sm mt-3 text-stone-700">{p.service_notes}</p>}
                  {p.review_summaries?.[0] && (
                    <div className="text-xs text-stone-500 mt-3 flex items-center gap-1">
                      <Star className="w-3 h-3 fill-amber-400 text-amber-400" />
                      <span className="font-medium text-stone-700">{p.review_summaries[0].rating}</span>
                      {p.review_summaries[0].count && <span>· {p.review_summaries[0].count.toLocaleString()} {p.review_summaries[0].source} reviews</span>}
                    </div>
                  )}
                </div>
                {p.website && (
                  <a href={p.website} target="_blank" rel="noopener noreferrer" className="text-stone-400 hover:text-stone-700"><ExternalLink className="w-4 h-4" /></a>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </section>

      <NominateProviderDialog
        open={nominateOpen}
        onClose={() => setNominateOpen(false)}
        onSubmitted={() => { setNominateOpen(false); toast.success('Thanks — admins will review shortly.'); }}
      />
    </div>
  );
}
